import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  Typography,
  Button,
  Tag,
  message,
  Modal,
} from "antd";
import {
  DeleteOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { decryptMessage } from "../../utils/decrypt";
import { getToken } from "../../services/auth";
import axios from "axios";
import {
  CONTEST_MANAGEMENT_BASE,
  API_BASE_URL,
} from "../../services/apiConstant";

const { Title } = Typography;

const placements = [
  {
    label: "1st Place",
    value: 1,
    icon: (
      <img
        src="../icons/winner_crown.svg"
        alt="1st Place"
        style={{ width: 10, height: 10, objectFit: "contain" }}
      />
    ),
    color: "#FFD700",
  },
  {
    label: "2nd Place",
    value: 2,
    icon: (
      <img
        src="../icons/winner_star.svg"
        alt="2nd Place"
        style={{ width: 10, height: 10, objectFit: "contain" }}
      />
    ),
    color: "#C0C0C0",
  },
  {
    label: "3rd Place",
    value: 3,
    icon: (
      <img
        src="../icons/winner_medal.svg"
        alt="3rd Place"
        style={{ width: 10, height: 10, objectFit: "contain" }}
      />
    ),
    color: "#CD7F32",
  },
];

const ParticipantDetails: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();

  const { participantId, contestId, fullname } =
    (location.state as {
      participantId?: string;
      contestId?: string;
      fullname?: string;
    }) || {};

  const [participant, setParticipant] = useState<any>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [winners, setWinners] = useState<{ [key: string]: string }>({});
  const [removed, setRemoved] = useState(false);
  const [loading, setLoading] = useState(true);

  const [isWinnerModalOpen, setIsWinnerModalOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<{
    id: string;
    value: string;
    label: string;
  } | null>(null);

  const [open, setOpen] = useState(false);

  // image preview modal
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    const fetchParticipantData = async () => {
      try {
        if (!participantId) return;

        const token = getToken();
        if (!token) throw new Error("No token found");

        const endpoint = `${API_BASE_URL + CONTEST_MANAGEMENT_BASE}/GetParticipantsWinnerImages/${participantId}`;
        const res = await axios.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.Data) {
          const decryptedData = decryptMessage(res.data.Data, token);
          if (decryptedData) {
            setParticipant(decryptedData);

            const uniquePhotos = decryptedData.filter(
              (photo: any, index: number, self: any[]) =>
                index ===
                self.findIndex(
                  (p) => p.id === photo.id && p.imageName === photo.imageName
                )
            );

            setPhotos(uniquePhotos || []);

            const mappedWinners: { [key: string]: string } = {};
            (uniquePhotos || []).forEach((photo: any) => {
              if (photo.position) {
                const place = placements.find(
                  (p) => p.value === photo.position
                )?.label;
                if (place) mappedWinners[photo.id] = place;
              }
            });
            setWinners(mappedWinners);
          }
        }
      } catch (err) {
        console.error("Error fetching participant data:", err);
        message.error("Failed to load participant details");
      } finally {
        setLoading(false);
      }
    };

    fetchParticipantData();
  }, [participantId]);

  const handleSetWinner = async (
    photoId: string,
    position: number,
    place: string
  ) => {
    try {
      const token = getToken();
      if (!token) throw new Error("No token found");

      const selectedPhoto = photos.find((p) => p.id === photoId);
      const payload = {
        participantID: selectedPhoto?.participantID,
        contestParticipantImageID: photoId,
        position: position,
      };

      await axios.post(
        `${API_BASE_URL + CONTEST_MANAGEMENT_BASE}/ContestWinner`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setWinners((prev) => ({ ...prev, [photoId]: place }));
      messageApi.success(`${place} saved successfully`);
    } catch (err: any) {
      console.error("Error saving winner:", err.response);
      messageApi.error(err.response?.data?.Message || "Failed to save winner");
    }
  };

  const showWinnerModal = (id: string, value: string, label: string) => {
    setSelectedPhoto({ id, value, label });
    setIsWinnerModalOpen(true);
  };

  const handleWinnerConfirm = () => {
    if (selectedPhoto) {
      handleSetWinner(selectedPhoto.id, selectedPhoto.value, selectedPhoto.label);
    }
    setIsWinnerModalOpen(false);
  };

  const handleWinnerCancel = () => {
    setIsWinnerModalOpen(false);
    setSelectedPhoto(null);
  };

  const handleImageClick = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setPreviewVisible(true);
  };

  const handleDelete = async () => {
    try {
      const token = getToken();
      if (!token) throw new Error("No token found");
      await axios.delete(`${API_BASE_URL}${CONTEST_MANAGEMENT_BASE}/ContestParticipants/${participantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      messageApi.success("Participant deleted successfully!");
      setOpen(false);
      setTimeout(() => {
        navigate(-1);
      }, 1000); // 1 second delay
    } catch (err: any) {
      console.error("Delete error:", err);
      messageApi.error("Failed to delete Participant.");
      messageApi.error(
        err.response?.data?.message || "Failed to delete Participant."
      );
    }
  };

  if (loading)
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <img
          src="/images/newLoading.gif"
          alt="Loading..."
          style={{ width: 200, height: 150 }}
        />
      </div>
    );

  return (
    <div style={{ padding: "20px" }}>
      {contextHolder}
      <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
        <Title level={3} style={{ color: "#41787C" }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined style={{ fontSize: 18, color: "#000" }} />}
            onClick={() => navigate(-1)}
            style={{ padding: 0, marginRight: 10 }}
          />
          {fullname}
        </Title>

        <Button
          type="primary"
          style={{ backgroundColor: "#2b7a78", borderColor: "#2b7a78" }}
          icon={<DeleteOutlined />}
          onClick={() => setOpen(true)}
        >
          Remove Participant
        </Button>

        <Modal
          title="Are you sure you want to remove this contestant?"
          open={open}
          onOk={handleDelete}
          onCancel={() => setOpen(false)}
          okText="Yes"
          cancelText="No"
          centered
        />
      </Row>

      {photos.length === 0 ? (
        <Title level={5} style={{ color: "#999" }}>
          No Images Available
        </Title>
      ) : (
        <Row gutter={[16, 16]}>
          {photos.map((photo) => (
            <Col key={photo.id} xs={24} sm={12}>
              <Card
                hoverable
                cover={
                  <img
                    alt={photo.label || "Photo"}
                    src={photo.fileUrl}
                    style={{ height: 250, objectFit: "cover", cursor: "pointer" }}
                    onClick={() => handleImageClick(photo.fileUrl)}
                  />
                }
              >
                <Tag
                  color={
                    placements.find((p) => p.label === winners[photo.id])?.color ||
                    "default"
                  }
                  icon={
                    placements.find((p) => p.label === winners[photo.id])?.icon ||
                    null
                  }
                  style={{ marginBottom: 12 }}
                >
                  {winners[photo.id]}
                </Tag>

                <Row gutter={8}>
                  {placements.map(({ label, value, icon, color }) => {
                    const isDisabled = !!winners[photo.id]; // disable if winner already marked
                    return (
                      <Col span={8} key={label}>
                        <Button
                          type={winners[photo.id] === label ? "primary" : "default"}
                          block
                          disabled={isDisabled}
                          style={{
                            height: 36,
                            fontSize: 14,
                            padding: "0 8px",
                            borderRadius: 8,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                            backgroundColor:
                              winners[photo.id] === label ? color : "#fff",
                            borderColor:
                              winners[photo.id] === label ? color : "#d9d9d9",
                            color:
                              winners[photo.id] === label ? "#fff" : "#000",
                          }}
                          onClick={() => showWinnerModal(photo.id, value, label)}
                        >
                          {icon && (
                            <img
                              src={icon.props.src}
                              alt={label}
                              style={{ width: 20, height: 20, objectFit: "contain" }}
                            />
                          )}
                          <span>{label}</span>
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ✅ Winner confirmation modal */}
      <Modal
        open={isWinnerModalOpen}
        onCancel={handleWinnerCancel}
        footer={null}
        closable={false}   // hides the default X close button
        mask={false}       // disables dark overlay
        maskClosable={false} // clicking outside does NOT close modal
        centered
        bodyStyle={{
          textAlign: "center",
          padding: "24px 16px",
          borderRadius: 12,
        }}
      >
        <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
          Mark as Winner
        </h3>
        <p style={{ fontSize: 14, color: "#555", marginBottom: 24 }}>
          Are you sure you want to mark{" "}
          <strong>{selectedPhoto?.label}</strong> as the winner?
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Button
            type="primary"
            block
            style={{
              backgroundColor: "#41787C",
              borderColor: "#41787C",
              height: 40,
              fontWeight: 500,
            }}
            onClick={handleWinnerConfirm}
          >
            Yes, Mark as Winner
          </Button>

          <Button
            block
            style={{
              backgroundColor: "#f2f2f2",
              border: "none",
              color: "#333",
              height: 40,
              fontWeight: 500,
            }}
            onClick={handleWinnerCancel}
          >
            Cancel
          </Button>
        </div>
      </Modal>

      {/* ✅ Image Preview Modal */}
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        centered
        width="80%"
        closable={true} // show the close button
        closeIcon={
          <span
            style={{
              color: "black",   // white color
              fontSize: 35,    // larger size if needed
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            ×
          </span>
        }
        bodyStyle={{
          textAlign: "center",
          background: "white", // background color
          padding: 0,
        }}
      >
        {previewImage && (
          <img
            alt="Preview"
            src={previewImage}
            style={{
              width: "100%",
              height: "auto",
              objectFit: "contain",
              maxHeight: "90vh",
            }}
          />
        )}
      </Modal>

    </div>
  );
};

export default ParticipantDetails;
